import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { POST } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import EntityConstants from "../../configs/EntityConstants";
import { useTranslation } from "react-i18next";
import { emailRegex, phoneNoRegex } from "../../configs/App.Regex";

export default function CreateOrganisationModal({
  isOpen,
  onClose,
  onOrganisationCreated,
  tenantCode,
}) {
  const { t } = useTranslation();
  const [orgName, setOrgName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    orgName: "",
    email: "",
    phone: "",
    orgType: "",
    paymentType: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const maxCharsOrgName = 100;
  const maxCharsEmail = 100;
  const maxCharsPhone = 15;

  const resetForm = () => {
    setOrgName("");
    setPhone("");
    setEmail("");
    setErrors({
      orgName: "",
      email: "",
      phone: "",
      orgType: "",
      paymentType: "",
    });
    setErrorMessage("");
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      orgName: "",
      email: "",
      phone: "",
      orgType: "",
      paymentType: "",
    };

    if (!orgName.trim()) {
      newErrors.orgName = t("tenant:organisation_name_required");
      valid = false;
    } else if (orgName.length > maxCharsOrgName) {
      newErrors.orgName = `${t(
        "tenant:organisation_name_must_be",
      )} ${maxCharsOrgName} ${t("tenant:characters_or_less")}`;
      valid = false;
    }

    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();

    if (!trimmedEmail) {
      newErrors.email = t("tenant:email_required");
      valid = false;
    } else if (!emailRegex.test(trimmedEmail)) {
      newErrors.email = t("tenant:valid_email_address");
      valid = false;
    } else if (trimmedEmail.length > maxCharsEmail) {
      newErrors.email = `${t("tenant:email_must_be")} ${maxCharsEmail} ${t(
        "tenant:characters_or_less",
      )}`;
      valid = false;
    }

    if (trimmedPhone && trimmedPhone.length > maxCharsPhone) {
      newErrors.phone = `${t(
        "tenant:phone_number_must_be",
      )} ${maxCharsPhone} ${t("tenant:characters_or_less")}`;
      valid = false;
    } else if (trimmedPhone && !phoneNoRegex.test(trimmedPhone)) {
      newErrors.phone = t("tenant:valid_phone_number");
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setErrorMessage("");

    const payload = {
      org_name: orgName.trim(),
      tenant_code: tenantCode,
      org_type: "organisation",
      email: email.trim(),
    };

    if (phone.trim()) {
      payload.phone = phone.trim();
    }

    POST(EntityConstants.POST_CREATE_TENANT_ORGANISATION, payload)
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          Toast({
            type: "success",
            title: t("tenant:success"),
            message:
              data.message || t("tenant:organisation_created_successfully"),
          });
          onOrganisationCreated();
          resetForm();
          onClose();
        } else {
          setErrorMessage(
            data.message || t("tenant:failed_create_organisation"),
          );
          Toast({
            type: "error",
            title: t("tenant:error"),
            message: data.message || t("tenant:failed_create_organisation"),
          });
        }
      })
      .catch((error) => {
        console.error("Create organisation error:", error);
        Toast({
          type: "error",
          title: t("tenant:error"),
          message: t("tenant:failed_create_organisation_retry"),
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                id="create-organisation-modal"
                className="max-w-xl transform overflow-hidden rounded-2xl bg-cfSecondary p-6 text-left align-middle shadow-xl transition-all w-full relative"
              >
                <form
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  <button
                    type="button"
                    id="create-organisation-modal-close"
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    &#10005;
                  </button>

                  <div className="">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-cfHeadingText"
                    >
                      {t("tenant:create_new_organisation")}
                    </Dialog.Title>
                    <p className="text-sm text-cfGlobalText mt-1">
                      {t("tenant:create_new_organisation_under_tenant")}:{" "}
                      {tenantCode}
                    </p>
                    <div className="space-y-4 mt-6">
                      <div>
                        <label className="block mb-2 text-sm font-medium text-cfHeadingText">
                          {t("tenant:organisation_name")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="create-organisation-modal-input-name"
                          className={`bg-cfTextInputBorder border ${
                            errors.orgName
                              ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
                              : "border-cfIssuedScreenSearchBorder"
                          } text-cfGlobalText text-sm rounded-lg focus:ring-cfButton focus:border-cfButton block p-2.5 w-full`}
                          placeholder="e.g. Ooru Digital"
                          value={orgName}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value.length <= maxCharsOrgName) {
                              setOrgName(value);
                              setErrors({ ...errors, orgName: "" });
                            }
                          }}
                        />
                        <div className="flex justify-between items-center mt-1">
                          {errors.orgName && (
                            <p
                              id="create-organisation-modal-error-name"
                              className="text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG"
                            >
                              {errors.orgName}
                            </p>
                          )}
                          <div className="text-right text-sm text-cfViewAllTagText ml-auto">
                            <span>
                              {maxCharsOrgName - orgName.length}{" "}
                              {t("tenant:characters_left")}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="w-full flex gap-2">
                        <div className="w-[50%]">
                          {errors.orgType && (
                            <p className="mt-1 text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG">
                              {errors.orgType}
                            </p>
                          )}
                        </div>

                        <div className="w-[50%]">
                          {errors.paymentType && (
                            <p className="mt-1 text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG">
                              {errors.paymentType}
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="create-organisation-modal-input-email"
                          className="block mb-2 text-sm font-medium text-cfHeadingText"
                        >
                          {t("tenant:email")}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="create-organisation-modal-input-email"
                          className={`bg-cfTextInputBorder border ${
                            errors.email
                              ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
                              : "border-cfIssuedScreenSearchBorder"
                          } text-cfGlobalText text-sm rounded-lg focus:ring-cfButton focus:border-cfButton block p-2.5 w-full`}
                          placeholder="e.g. contact@organisation.com"
                          value={email}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value.length <= maxCharsEmail) {
                              setEmail(value);
                              setErrors({ ...errors, email: "" });
                            }
                          }}
                        />
                        <div className="flex justify-between items-center mt-1">
                          {errors.email && (
                            <p
                              id="create-organisation-modal-error-email"
                              className="text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG"
                            >
                              {errors.email}
                            </p>
                          )}
                          <div className="text-right text-sm text-cfViewAllTagText ml-auto">
                            <span>
                              {maxCharsEmail - email.length}{" "}
                              {t("tenant:characters_left")}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="create-organisation-modal-input-phone"
                          className="block mb-2 text-sm font-medium text-cfHeadingText"
                        >
                          {t("tenant:phone_optional")}
                        </label>
                        <input
                          type="tel"
                          id="create-organisation-modal-input-phone"
                          className={`bg-cfTextInputBorder border ${
                            errors.phone
                              ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
                              : "border-cfIssuedScreenSearchBorder"
                          } text-cfGlobalText text-sm rounded-lg focus:ring-cfButton focus:border-cfButton block p-2.5 w-full`}
                          placeholder="e.g. 9991112222"
                          value={phone}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            if (value.length <= maxCharsPhone) {
                              setPhone(value);
                              setErrors({ ...errors, phone: "" });
                            }
                          }}
                        />
                        <div className="flex justify-between items-center mt-1">
                          {errors.phone && (
                            <p
                              id="create-organisation-modal-error-phone"
                              className="text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG"
                            >
                              {errors.phone}
                            </p>
                          )}
                          <div className="text-right text-sm text-cfViewAllTagText ml-auto">
                            <span>
                              {maxCharsPhone - phone.length}{" "}
                              {t("tenant:characters_left")}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    {errorMessage && (
                      <p
                        id="create-organisation-modal-error-message"
                        className="mt-4 text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG flex items-center"
                      >
                        <span className="mr-1 text-base leading-none">*</span>
                        {errorMessage}
                      </p>
                    )}
                  </div>

                  <div className="flex mt-8 justify-end gap-4">
                    <button
                      type="button"
                      id="create-organisation-modal-cancel"
                      className="px-4 py-2 text-cfHeadingText border hover:bg-cfCardHover transition-all border-cfHeadingText rounded-[8px] focus:outline-none focus:ring-3 focus:ring-offset-2"
                      onClick={handleClose}
                      disabled={isLoading}
                    >
                      {t("tenant:cancel")}
                    </button>
                    <button
                      type="submit"
                      id="create-organisation-modal-submit"
                      className="px-4 py-2 bg-cfButton hover:bg-cfPrimaryDark text-cfSecondary font-semibold border transition-all border-cfHeadingText rounded-[8px] focus:outline-none focus:ring-3 focus:ring-offset-2 flex items-center justify-center min-w-[120px]"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          {t("tenant:creating")}
                        </span>
                      ) : (
                        t("tenant:create_organisation_button")
                      )}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
